import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  SignalCellularAlt as SignalIcon,
  DeviceHub as DeviceHubIcon,
  ThermostatAuto as TempIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '70vh',
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    zIndex: 1
  }
}));

const SignalChip = styled(Chip)<{ signaltype: string }>(({ signaltype, theme }) => ({
  ...(signaltype === 'good' && {
    backgroundColor: '#38B900',
    color: 'white'
  }),
  ...(signaltype === 'low' && {
    backgroundColor: '#FF82BD',
    color: 'white'
  }),
  ...(signaltype === 'too_low' && {
    backgroundColor: '#FF0000',
    color: 'white'
  }),
  ...(signaltype === 'too_strong' && {
    backgroundColor: '#FFA817',
    color: 'white'
  }),
  ...(signaltype === 'unknown' && {
    backgroundColor: theme.palette.grey[500],
    color: 'white'
  })
}));

const StatCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}25 100%)`,
  border: `1px solid ${theme.palette.primary.main}30`
}));

interface ONU {
  onuId: string;
  name: string | null;
  macAddress: string;
  status: string;
  version: string;
  chipId: string;
  portNumber: number;
  temperature: number;
  voltage: number;
  current: number;
  txPower: number;
  rxPower: number | null;
  distance: number;
  rawDistance: number;
  signalQuality: string;
  isOnline: boolean;
}

interface ONUStatistics {
  total: number;
  online: number;
  offline: number;
  signalQuality: {
    good: number;
    low: number;
    too_low: number;
    too_strong: number;
    unknown: number;
  };
  averageDistance: number;
  averageRxPower: number | null;
  temperatureRange: {
    min: number | null;
    max: number | null;
    average: number | null;
  };
}

interface ONUData {
  olt: {
    id: string;
    type: string;
    ip_address: string;
    port: number;
    spop: {
      id: string;
      name: string;
      areaCode: string;
    };
  };
  ponId: string;
  onus: ONU[];
  statistics: ONUStatistics;
  retrievedAt: string;
}

interface ONUDataViewProps {
  oltId: string;
  ponId: string;
  onPonIdChange: (ponId: string) => void;
}

const ONUDataView: React.FC<ONUDataViewProps> = ({ oltId, ponId, onPonIdChange }) => {
  const [data, setData] = useState<ONUData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [signalFilter, setSignalFilter] = useState<string>('all');

  const fetchONUData = async () => {
    if (!oltId || !ponId) return;

    setLoading(true);
    setError(null);

    try {
      // Gunakan axios instance dari api.ts
      const response = await (await import('../services/api')).default.get(`/olts/${oltId}/onu-data`, {
        params: { ponId }
      });
      setData(response.data.data);
    } catch (err: any) {
      console.error('Error fetching ONU data:', err);
      setError(err?.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (oltId && ponId) {
      fetchONUData();
    }
  }, [oltId, ponId]);

  const handleRefresh = () => {
    fetchONUData();
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };

  const handleSignalFilterChange = (event: SelectChangeEvent<string>) => {
    setSignalFilter(event.target.value);
  };

  const getFilteredONUs = () => {
    if (!data) return [];
    
    return data.onus.filter(onu => {
      const statusMatch = statusFilter === 'all' || 
        (statusFilter === 'online' && onu.isOnline) ||
        (statusFilter === 'offline' && !onu.isOnline);
      
      const signalMatch = signalFilter === 'all' || onu.signalQuality === signalFilter;
      
      return statusMatch && signalMatch;
    });
  };

  const getSignalLabel = (quality: string) => {
    switch (quality) {
      case 'good': return 'Good';
      case 'low': return 'Low';
      case 'too_low': return 'Too Low';
      case 'too_strong': return 'Too Strong';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading ONU data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
        <Button onClick={handleRefresh} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary">
          Select PON ID to view ONU data
        </Typography>
      </Box>
    );
  }

  const filteredONUs = getFilteredONUs();

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          ONU Data - PON {data.ponId}
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            value={ponId}
            onChange={(e) => onPonIdChange(e.target.value)}
            label="PON ID"
            placeholder="e.g., 0/1/1"
            size="small"
            sx={{ width: 120 }}
          />
          <Tooltip title="Refresh ONU Data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2} mb={3}>
        <StatCard>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <DeviceHubIcon color="primary" />
              <Typography variant="h6">Total ONUs</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {data.statistics.total}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Online: {data.statistics.online} | Offline: {data.statistics.offline}
            </Typography>
          </CardContent>
        </StatCard>

        <StatCard>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <SignalIcon color="primary" />
              <Typography variant="h6">Signal Quality</Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              <Chip size="small" label={`Good: ${data.statistics.signalQuality.good}`} 
                    sx={{ backgroundColor: '#38B900', color: 'white' }} />
              <Chip size="small" label={`Low: ${data.statistics.signalQuality.low}`} 
                    sx={{ backgroundColor: '#FF82BD', color: 'white' }} />
              <Chip size="small" label={`Too Low: ${data.statistics.signalQuality.too_low}`} 
                    sx={{ backgroundColor: '#FF0000', color: 'white' }} />
            </Box>
          </CardContent>
        </StatCard>

        <StatCard>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <SpeedIcon color="primary" />
              <Typography variant="h6">Avg Distance</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {data.statistics.averageDistance}m
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {data.statistics.averageRxPower !== null 
                ? `Avg RX: ${data.statistics.averageRxPower} dBm`
                : 'No RX Power data'
              }
            </Typography>
          </CardContent>
        </StatCard>

        <StatCard>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <TempIcon color="primary" />
              <Typography variant="h6">Temperature</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {data.statistics.temperatureRange.average || 0}°C
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Range: {data.statistics.temperatureRange.min || 0}°C - {data.statistics.temperatureRange.max || 0}°C
            </Typography>
          </CardContent>
        </StatCard>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Signal Quality</InputLabel>
          <Select value={signalFilter} onChange={handleSignalFilterChange} label="Signal Quality">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="good">Good</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="too_low">Too Low</MenuItem>
            <MenuItem value="too_strong">Too Strong</MenuItem>
            <MenuItem value="unknown">Unknown</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* ONU Table */}
      <Paper>
        <StyledTableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ONU ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>MAC Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Signal Quality</TableCell>
                <TableCell align="right">Distance (m)</TableCell>
                <TableCell align="right">Temperature (°C)</TableCell>
                <TableCell align="right">TX Power (dBm)</TableCell>
                <TableCell align="right">RX Power (dBm)</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Chip ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredONUs.map((onu, index) => (
                <TableRow 
                  key={onu.onuId} 
                  hover
                  sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                    backgroundColor: onu.isOnline ? 'inherit' : 'action.disabled'
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight="medium">
                      {onu.onuId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {onu.name || (
                      <Typography variant="body2" color="textSecondary" fontStyle="italic">
                        Not Set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {onu.macAddress}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={onu.status}
                      color={onu.isOnline ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <SignalChip 
                      label={getSignalLabel(onu.signalQuality)}
                      signaltype={onu.signalQuality}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {onu.distance}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {onu.temperature.toFixed(0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {onu.txPower.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {onu.rxPower !== null ? onu.rxPower.toFixed(2) : '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {onu.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {onu.chipId}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Paper>

      {filteredONUs.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="textSecondary">
            No ONUs found matching the current filters
          </Typography>
        </Box>
      )}

      <Box mt={2}>
        <Typography variant="caption" color="textSecondary">
          Last updated: {new Date(data.retrievedAt).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default ONUDataView;
